import { Link, useParams } from "react-router-dom";

import { linkTo } from "../api/links";
import { useAcks, useBatches, useChanges, useCursors, useFeed } from "../api/queries";
import type { Cursor } from "../api/types";
import { AckTable } from "../components/AckTable";
import { BatchTable } from "../components/BatchTable";
import { FeedTable } from "../components/FeedTable";
import { LoadMore } from "../components/MasterDetail";
import {
  Badge,
  Empty,
  ErrorBox,
  EventType,
  Field,
  FieldGrid,
  Guid,
  Num,
  OutcomeBadge,
  Panel,
  ReasonBadge,
  SEQ,
  Spinner,
  Time,
} from "../components/primitives";

/**
 * Sync health for one driver, assembled from the bounded tables. There is no Sync Snapshot
 * store and no presence signal yet, so this screen answers "where is their cursor and what
 * has their device acked", not "are they online".
 */
export function DriverDetail() {
  const { driverGuid = "" } = useParams();
  const cursors = useCursors(driverGuid);
  const rows = cursors.data?.cursors ?? [];
  const carriers = [...new Set(rows.map((row) => row.carrier_guid))];

  return (
    <div className="h-full space-y-4 overflow-y-auto p-5">
      <div className="flex flex-wrap items-baseline gap-x-4 gap-y-2">
        <span className="label">driver</span>
        <h1 className="mono text-base text-strong">{driverGuid}</h1>
        <Link to={linkTo.driverLoads(driverGuid)} className="ml-auto label hover:text-strong">
          loads →
        </Link>
        <Link to={linkTo.driverBatches(driverGuid)} className="label hover:text-strong">
          all batches →
        </Link>
      </div>

      <Panel title="Connections" id="driver-connections">
        {cursors.isPending && <Spinner />}
        {cursors.error && <ErrorBox error={cursors.error} />}
        {cursors.isSuccess && rows.length === 0 && (
          <Empty>No cursor allocated — nothing has ever been applied for this driver.</Empty>
        )}
        {rows.map((row) => (
          <ConnectionRow key={row.id} cursor={row} />
        ))}
      </Panel>

      {carriers.map((carrier) => (
        <CarrierFeed key={carrier} driverGuid={driverGuid} carrierGuid={carrier} />
      ))}

      <DriverAcks driverGuid={driverGuid} />

      <div className="grid gap-4 2xl:grid-cols-2">
        <DriverJournal driverGuid={driverGuid} />
        <DriverBatches driverGuid={driverGuid} />
      </div>
    </div>
  );
}

function ConnectionRow({ cursor }: { cursor: Cursor }) {
  return (
    <FieldGrid className="border-b border-line/70 last:border-b-0">
      <Field label="root type">
        <Badge tone="info">{cursor.root_type}</Badge>
      </Field>
      <Field label="ceiling">
        <span className="mono text-xs" title={SEQ.ceiling}>
          {cursor.last_seq}
        </span>
      </Field>
      <Field label="carrier">
        <Guid value={cursor.carrier_guid} to={linkTo.carrier(cursor.carrier_guid)} />
      </Field>
    </FieldGrid>
  );
}

function CarrierFeed({ driverGuid, carrierGuid }: { driverGuid: string; carrierGuid: string }) {
  const query = useFeed({ driver_guid: driverGuid, carrier_guid: carrierGuid });
  const feed = query.data?.pages.flatMap((page) => page.feed) ?? [];

  return (
    <Panel
      id={`driver-feed-${carrierGuid}`}
      title={`Feed · ${feed.length}${query.hasNextPage ? "+" : ""} loads`}
      actions={
        <span className="flex items-center gap-2">
          <span className="label">carrier</span>
          <Guid value={carrierGuid} to={linkTo.carrier(carrierGuid)} />
        </span>
      }
    >
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && (
        <FeedTable
          feed={feed}
          omit={["driver", "carrier"]}
          empty="An empty feed — this connection's device pull returns nothing."
        />
      )}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}

function DriverAcks({ driverGuid }: { driverGuid: string }) {
  const query = useAcks({ driver_guid: driverGuid });
  const acks = query.data?.pages.flatMap((page) => page.acks) ?? [];

  return (
    <Panel id="driver-acks" title={`Device acks (${acks.length}${query.hasNextPage ? "+" : ""})`}>
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && (
        <AckTable acks={acks} omit={["driver"]} empty="No device has acked anything." />
      )}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}

function DriverJournal({ driverGuid }: { driverGuid: string }) {
  const query = useChanges({ driver_guid: driverGuid });
  const changes = query.data?.pages.flatMap((page) => page.changes) ?? [];

  return (
    <Panel id="driver-journal" title={`Journal (${changes.length}${query.hasNextPage ? "+" : ""})`}>
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && changes.length === 0 && <Empty>No journal rows for this driver.</Empty>}
      {changes.map((entry) => (
        <div key={entry.seq} className="row row-hover flex flex-wrap items-center gap-3">
          <Num label="id" value={entry.seq} hint={SEQ.id} />
          <OutcomeBadge outcome={entry.outcome} />
          <ReasonBadge code={entry.reason_code} outcome={entry.outcome} />
          <EventType value={entry.event_type} />
          <span className="text-sm text-strong">{entry.entity_type}</span>
          <Guid
            value={entry.root_guid}
            to={linkTo.rootTimeline(entry.root_type, entry.root_guid)}
          />
          <span className="ml-auto">
            <Time value={entry.created_at} />
          </span>
        </div>
      ))}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}

function DriverBatches({ driverGuid }: { driverGuid: string }) {
  const query = useBatches({ driver_guid: driverGuid });
  const batches = query.data?.pages.flatMap((page) => page.batches) ?? [];

  return (
    <Panel id="driver-batches" title={`Batches (${batches.length}${query.hasNextPage ? "+" : ""})`}>
      {query.isPending && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}
      {query.isSuccess && (
        <BatchTable
          batches={batches}
          omit={["driver"]}
          empty="Nothing in the inbox from this driver."
        />
      )}
      <LoadMore
        hasNext={query.hasNextPage}
        loading={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      />
    </Panel>
  );
}
